import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiPlus, FiEdit2, FiCopy, FiEye, FiArchive,
  FiToggleLeft, FiToggleRight, FiSearch,
  FiChevronLeft, FiChevronRight,
} from "react-icons/fi";
import API from "../../utils/api";
import toast from "react-hot-toast";
import "../AdminCRM.css";

const STATUS_COLOR = {
  draft:    { bg: "#f1f5f9", text: "#64748b" },
  active:   { bg: "#dcfce7", text: "#15803d" },
  inactive: { bg: "#fef9c3", text: "#a16207" },
  archived: { bg: "#f1f5f9", text: "#94a3b8" },
};

export default function AdminAllProducts() {
  const navigate = useNavigate();

  const [products, setProducts]     = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [total, setTotal]           = useState(0);
  const [pages, setPages]           = useState(1);

  const [search, setSearch]   = useState("");
  const [filters, setFilters] = useState({ categoryId: "", subcategoryId: "", status: "", featured: "" });
  const [sort, setSort]       = useState("newest");
  const [page, setPage]       = useState(1);
  const [showFilters, setShowFilters] = useState(false); // kept for compatibility

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page, limit: 20, sort,
        ...(search && { search }),
        ...(filters.categoryId    && { categoryId:    filters.categoryId    }),
        ...(filters.subcategoryId && { subcategoryId: filters.subcategoryId }),
        ...(filters.status        && { status:        filters.status        }),
        ...(filters.featured      && { featured:      filters.featured      }),
      });
      const { data } = await API.get(`/catalog/products?${params}`);
      setProducts(data.products || []);
      setTotal(data.count || 0);
      setPages(data.pages || 1);
    } catch { toast.error("Failed to load products"); }
    finally { setLoading(false); }
  }, [page, sort, search, filters]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    API.get("/catalog/categories").then(r => setCategories(r.data.categories || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!filters.categoryId) { setSubcategories([]); return; }
    API.get(`/catalog/subcategories?categoryId=${filters.categoryId}`)
      .then(r => setSubcategories(r.data.subcategories || []))
      .catch(() => {});
  }, [filters.categoryId]);

  const handleSearch = (e) => { setSearch(e.target.value); setPage(1); };
  const handleFilter = (k, v) => { setFilters(f => ({ ...f, [k]: v, ...(k === "categoryId" ? { subcategoryId: "" } : {}) })); setPage(1); };
  const clearFilters = () => { setFilters({ categoryId: "", subcategoryId: "", status: "", featured: "" }); setSearch(""); setPage(1); };

  const toggleStatus = async (p) => {
    const newStatus = p.status === "active" ? "inactive" : "active";
    try {
      await API.put(`/catalog/products/${p.id}`, { status: newStatus });
      toast.success(`Product ${newStatus}`);
      load();
    } catch { toast.error("Failed"); }
  };

  const archive = async (p) => {
    if (!window.confirm(`Archive "${p.name}"?`)) return;
    try {
      await API.patch(`/catalog/products/${p.id}/archive`);
      toast.success("Product archived");
      load();
    } catch { toast.error("Failed"); }
  };

  const duplicate = async (p) => {
    try {
      const { data } = await API.post(`/catalog/products/${p.id}/duplicate`);
      toast.success("Product duplicated");
      navigate(`/admin/products/${data.productId}/edit`);
    } catch (e) { toast.error(e.response?.data?.message || "Failed"); }
  };

  const activeFiltersCount = Object.values(filters).filter(Boolean).length;

  return (
    <div style={{ padding: "24px 28px" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 22 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>Products</h2>
          <p style={{ margin: 0, fontSize: 13, color: "#64748b", marginTop: 2 }}>{total} products</p>
        </div>
        <button onClick={() => navigate("/admin/products/add")} style={primaryBtn}>
          <FiPlus size={14} style={{ marginRight: 6 }} />Add Product
        </button>
      </div>

      {/* Search + inline filters — always visible */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ position: "relative", flex: "1 1 260px" }}>
          <FiSearch style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#9ca3af" }} size={14} />
          <input
            style={{ width: "100%", boxSizing: "border-box", padding: "8px 10px 8px 32px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 14, outline: "none" }}
            placeholder="Search by name, SKU, tags…"
            value={search}
            onChange={handleSearch}
          />
        </div>

        <select style={{ padding: "8px 10px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 14, background: "#fff", cursor: "pointer" }} value={filters.categoryId} onChange={e => handleFilter("categoryId", e.target.value)}>
          <option value="">All Categories</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>

        <select style={{ padding: "8px 10px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 14, background: "#fff", cursor: "pointer" }} value={filters.subcategoryId} onChange={e => handleFilter("subcategoryId", e.target.value)} disabled={!filters.categoryId}>
          <option value="">All Subcategories</option>
          {subcategories.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>

        <select style={{ padding: "8px 10px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 14, background: "#fff", cursor: "pointer" }} value={filters.status} onChange={e => handleFilter("status", e.target.value)}>
          <option value="">All Status</option>
          <option value="draft">Draft</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="archived">Archived</option>
        </select>

        <select style={{ padding: "8px 10px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 14, background: "#fff", cursor: "pointer" }} value={filters.featured} onChange={e => handleFilter("featured", e.target.value)}>
          <option value="">All Products</option>
          <option value="true">Featured Only</option>
        </select>

        <select style={{ padding: "8px 10px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 14, background: "#fff", cursor: "pointer" }} value={sort} onChange={e => setSort(e.target.value)}>
          <option value="newest">Newest</option>
          <option value="oldest">Oldest</option>
          <option value="name">Name A–Z</option>
          <option value="price">Price ↑</option>
          <option value="order">Display Order</option>
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 60, color: "#94a3b8" }}>Loading…</div>
      ) : products.length === 0 ? (
        <div style={{ textAlign: "center", padding: 60, color: "#94a3b8" }}>No products found.</div>
      ) : (
        <div className="crm-card" style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: "2px solid #f3f4f6", background: "#fafafa" }}>
                  {["Image", "Product", "SKU", "Category", "Starting Price", "Status", "Featured", "Updated", "Actions"].map(h => (
                    <th key={h} style={{ textAlign: "left", padding: "10px 14px", fontWeight: 600, fontSize: 11, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {products.map(p => (
                  <tr key={p.id} style={{ borderBottom: "1px solid #f3f4f6" }} onMouseEnter={e => e.currentTarget.style.background = "#f9fafb"} onMouseLeave={e => e.currentTarget.style.background = ""}>
                    <td style={{ padding: "10px 14px" }}>
                      {p.mainImage
                        ? <img src={p.mainImage} alt="" style={{ width: 40, height: 40, borderRadius: 6, objectFit: "cover" }} />
                        : <div style={{ width: 40, height: 40, borderRadius: 6, background: "#f1f5f9" }} />
                      }
                    </td>
                    <td style={{ padding: "10px 14px", fontWeight: 600, maxWidth: 200 }}>
                      <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</div>
                      {p.subcategory && <div style={{ fontSize: 11, color: "#94a3b8" }}>{p.subcategory.name}</div>}
                    </td>
                    <td style={{ padding: "10px 14px", color: "#64748b", fontFamily: "monospace" }}>{p.sku}</td>
                    <td style={{ padding: "10px 14px", color: "#64748b" }}>{p.category?.name || "—"}</td>
                    <td style={{ padding: "10px 14px" }}>
                      {p.startingPrice != null
                        ? <span style={{ fontWeight: 600 }}>₹{parseFloat(p.startingPrice).toLocaleString("en-IN")}</span>
                        : <span style={{ color: "#94a3b8", fontSize: 11 }}>Price on Request</span>}
                    </td>
                    <td style={{ padding: "10px 14px" }}>
                      <span style={{ fontSize: 11, fontWeight: 600, background: STATUS_COLOR[p.status]?.bg, color: STATUS_COLOR[p.status]?.text, padding: "2px 8px", borderRadius: 20 }}>{p.status}</span>
                    </td>
                    <td style={{ padding: "10px 14px", textAlign: "center" }}>
                      {p.isFeatured ? <span style={{ color: "#f59e0b" }}>★</span> : <span style={{ color: "#e2e8f0" }}>★</span>}
                    </td>
                    <td style={{ padding: "10px 14px", color: "#94a3b8", whiteSpace: "nowrap" }}>
                      {new Date(p.updatedAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}
                    </td>
                    <td style={{ padding: "10px 14px" }}>
                      <div style={{ display: "flex", gap: 4 }}>
                        <button title="Edit" onClick={() => navigate(`/admin/products/${p.id}/edit`)} style={iconBtn}><FiEdit2 size={13} /></button>
                        <button title="Duplicate" onClick={() => duplicate(p)} style={iconBtn}><FiCopy size={13} /></button>
                        <button title="Preview" onClick={() => navigate(`/admin/products/${p.id}/preview`)} style={iconBtn}><FiEye size={13} /></button>
                        {p.status !== "archived" && (
                          <>
                            <button title={p.status === "active" ? "Deactivate" : "Activate"} onClick={() => toggleStatus(p)} style={iconBtn}>
                              {p.status === "active" ? <FiToggleRight size={15} color="#22c55e" /> : <FiToggleLeft size={15} color="#94a3b8" />}
                            </button>
                            <button title="Archive" onClick={() => archive(p)} style={iconBtn}><FiArchive size={13} color="#f59e0b" /></button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginTop: 20 }}>
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={iconBtn}><FiChevronLeft /></button>
          <span style={{ fontSize: 13, color: "#64748b" }}>Page {page} of {pages}</span>
          <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages} style={iconBtn}><FiChevronRight /></button>
        </div>
      )}
    </div>
  );
}

/* ─── Styles ─────────────────────────────────────────────────────── */
const inp = { padding: "8px 10px", border: "1px solid #e2e8f0", borderRadius: 7, fontSize: 13, boxSizing: "border-box", outline: "none", background: "#fff" };
const lbl = { display: "block", fontSize: 11, fontWeight: 600, color: "#475569", marginBottom: 4 };
const primaryBtn = { display: "inline-flex", alignItems: "center", background: "var(--primary,#6366f1)", color: "#fff", border: "none", borderRadius: 7, padding: "8px 16px", fontSize: 13, cursor: "pointer", fontWeight: 500 };
const secBtn     = { display: "inline-flex", alignItems: "center", background: "#f1f5f9", color: "#374151", border: "none", borderRadius: 7, padding: "8px 14px", fontSize: 13, cursor: "pointer", fontWeight: 500, position: "relative" };
const iconBtn    = { background: "none", border: "none", cursor: "pointer", color: "#64748b", padding: 5, display: "flex", alignItems: "center", borderRadius: 5 };